import type { PersonalPageItemLabels } from '../labels';
import type { PersonalPageSubtype } from '../subtypes';
import type { MaxNumOfPaymentsMode } from '../../pageCreditInstallments';

export type DefineStepChoiceIcon = 'volunteer' | 'confirmation' | 'registration';


export interface PersonalPageDefineStepChoiceMeta {
  label: string;
  helpText: string;
  icon: DefineStepChoiceIcon;
}

export interface PersonalPageDefineStepChoiceOption extends PersonalPageDefineStepChoiceMeta {
  subtype: PersonalPageSubtype;
  registrationType: 'form' | 'tickets';
}

export interface PersonalPageTicketSectionLabels {
  title: string;
  description: string;
  buttonText: string;
  emptyText: string;
  addTitle: string;
  editTitle: string;
  deleteTitle: string;
  deleteConfirmTitle: string;
  priceRowLabel: string;
  inventoryRowLabel: string;
  amountFieldLabel: string;
}

export interface PersonalPageDefineStepItemActionMessages {
  added: string;
  updated: string;
  deleted: string;
  duplicated: string;
  notFound: string;
  cannotDelete: string;
}

export interface PersonalPageDefineStepAccordionLabels {
  pageTitle: string;
  pageIntro: string;
  scheduleCardTitle: string;
  scheduleCardDescriptionWithRange: string;
  scheduleCardDescriptionNameOnly: string;
  pageNameLabel: string;
  pageNamePlaceholder: string;
  startRangeTitle: string;
  endRangeTitle: string;
  clearTimeRangeLabel: string;
  unitsCardTitle: string;
  unitsCardTitleWithInventory: string;
  unitsCardDescription: string;
  unitsCardDescriptionWithInventory: string;
  addUnitButton: string;
  unitNameLabel: string;
  unitNamePlaceholder: string;
  unitAmountLabel: string;
  unitInventoryLabel: string;
  deleteUnitAriaLabel: string;
  openAmountTitle: string;
  openAmountDescription: string;
  openAmountCreditRecurringTitle: string;
  openAmountCreditRecurringSwitchLabel: string;
  openAmountCreditRecurringDescription: string;
  soldRemainingPrompt: string;
  soldCountButton: string;
  remainingCountButton: string;
  paymentCardTitle: string;
  paymentCardOptionInstallments: string;
  paymentCardOptionProvider: string;
  paymentCardOptionCurrencies: string;
  paymentCardOptionPaymentMethods: string;
  paymentProviderLabel: string;
  displayCardTitle: string;
  displayCardDescription: string;
  showGoalTitle: string;
  showGoalDescription: string;
  goalAmountLabel: string;
  showCountdownTitle: string;
  showCountdownDescription: string;
  showAllParticipantsTitle: string;
  showAllParticipantsDescription: string;
  showRecentContributionsTitle: string;
  showRecentContributionsDescription: string;
  multiplierTitle: string;
  multiplierDescription: string;
  multiplierFactorLabel: string;
  multiplierFactorAriaLabel: string;
  pageLimitsCardTitle: string;
  pageLimitsCardDescription: string;
  maxQuotaLabel: string;
  advancedTicketsCardTitle: string;
  advancedTicketsCardDescription: string;
  advancedTicketsCouponsHint: string;
  pagePerBuyerLimitTitle: string;
  pagePerBuyerLimitMaxLabel: string;
  pagePerBuyerLimitHint: string;
  eventPaymentModeLabel: string;
  groupsCardTitle: string;
  groupsCardDescription: string;
  formCardTitle: string;
  formCardDescription: string;
  shareCardTitle: string;
  shareCardDescription: string;
  ogTitleLabel: string;
  ogTitlePlaceholder: string;
  ogDescriptionLabel: string;
  ogDescriptionPlaceholder: string;
  ogImageLabel: string;
  ogImageAlt: string;
  ogImagePlaceholder: string;
}

export interface PersonalPageDefineStepLabels {
  formSubject: string;
  formFieldsTitle: string;
  formFieldsSubtitle: string;
  introText?: string;
  choiceSectionTitle?: string;
  emptyChoicePrompt: string;
  cannotChangeChoiceMessage: string;
  registrationLimitTitle: string;
  registrationLimitSubtitle: string;
  perBuyerLimitTitle: string;
  perBuyerLimitMaxLabel: string;
  perBuyerLimitHint: string;
  ticketSection: PersonalPageTicketSectionLabels;
  itemActionMessages: PersonalPageDefineStepItemActionMessages;
  accordion: PersonalPageDefineStepAccordionLabels;
}

export type PersonalPageDefineStepLabelOverrides = Partial<
  Omit<PersonalPageDefineStepLabels, 'accordion'>
> & {
  accordion?: Partial<PersonalPageDefineStepAccordionLabels>;
};

export interface PersonalPageDefineStepLabelConfig {
  formSubject: string;
  overrides?: PersonalPageDefineStepLabelOverrides;
}

export type PersonalPageItemKind = 'none' | 'donationUnit' | 'ticket' | 'registrantType' | 'service';

export type PersonalPageDataDestination = 'campaigns' | 'registrations' | 'registrationsAndLeads' | 'none';

export type DefineStepAccordionSectionId =
  | 'schedule'
  | 'units'
  | 'payment'
  | 'display'
  | 'pageLimits'
  | 'advancedTickets'
  | 'groups'
  | 'donorForm'
  | 'shareMeta';

export const DEFAULT_DEFINE_STEP_ACCORDION_ORDER: DefineStepAccordionSectionId[] = [
  'schedule',
  'units',
  'payment',
  'display',
  'pageLimits',
  'groups',
  'donorForm',
  'shareMeta'
];

export interface PersonalPageItemOptions {
  inventory: boolean;
  /** Whether items expose a price/amount field (false = always free) */
  payments: boolean;
  /** Whether the price/amount field is required and must be at least 1 */
  paymentsRequired: boolean;
  coupon: boolean;
  perBuyerLimit: boolean;
  recurringOnItem: boolean;
  /** Per-item toggle in serviceRegistration — hides quantity in public flow */
  singular: boolean;
}

export interface PersonalPageCapabilities {
  hasDefineStep: boolean;
  hasLocation: boolean;
  hasDateTime: boolean;
  /** When true, start/end activation range can be defined in Define (optional). When false, no start/end range UI. */
  hasStartEndRange: boolean;
  hasGoalProgress: boolean;
  hasRecentDonations: boolean;
  hasAllDonations: boolean;
  hasGoal: boolean;
  supportsGroups: boolean;
  requiresDefinedItems: boolean;
  itemKind: PersonalPageItemKind;
  itemOptions: PersonalPageItemOptions;
  soldRemainingDisplay: boolean;
  allowDonationAtEnd: boolean;
  allowFreeRecurringDonation: boolean;
  dataDestination: PersonalPageDataDestination;
  completionMessages: { inApp: React.ReactNode; sms: string; email: string };
  allowSubtypeChoice?: boolean;
  subtypeChoiceOptions?: PersonalPageSubtype[];
  itemLabels: PersonalPageItemLabels;
  label: string;
  defineStepLabelConfig: PersonalPageDefineStepLabelConfig;
  defineStepChoice?: PersonalPageDefineStepChoiceMeta;
  defineStepAccordionOrder?: DefineStepAccordionSectionId[];
}

export interface PersonalPageEstablishValidationInput {
  pageType?: string;
  pageSubtype?: PersonalPageSubtype;
  registrationType?: 'form' | 'tickets';
  paymentProviderPreference?: 'personal' | 'system' | null;
  personalPaymentProviderId?: number | null;
  tickets?: unknown[];
  campaignData?: {
    openDonation?: {
      showOpenAmountField?: boolean;
      fixedAmounts?: Array<{
        name?: string;
        amount?: number;
        isRecurring?: boolean;
        recurringNumberOfPayments?: number | null;
      }>;
    };
  };
  activeDate?: Date | string | null;
  expiryDate?: Date | string | null;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  eventTicketPaymentMode?: string | null;
  maxNumOfPaymentsMode?: MaxNumOfPaymentsMode;
  maxNumOfPayments?: number;
  maxNumOfPaymentsRangeMax?: number;
}
